/**
 * Catalog Quick Search — the one rule both job pickers use (Create Ticket and
 * Add Job).
 *
 * The pickers used to substring-match the label, category key and submenu
 * only. "door module" is not a substring of "Door Control Module", and the
 * catalog's aliases (e.g. "Door module programming", "DCM replacement") were
 * never searched, so a job that was in the catalog could not be found by the
 * words people actually type.
 *
 * Now: every word of the query must appear somewhere in the job's searchable
 * text — label, submenu, category (key and display form) and aliases —
 * case-insensitively; a code matches by whole segment. A single word behaves exactly like the old substring
 * match on those fields.
 */

export interface SearchableCatalogJob {
  label: string;
  category_key: string;
  sub_menu: string;
  code?: string | null;
  aliases?: unknown;
}

function aliasText(aliases: unknown): string {
  if (Array.isArray(aliases)) return aliases.filter((a) => typeof a === 'string').join(' ');
  return typeof aliases === 'string' ? aliases : '';
}

/** `heatingVentilationAC` → "heating ventilation ac", `body_frame` → "body frame". */
function categoryWords(key: string): string {
  return key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

export function catalogJobSearchText(job: SearchableCatalogJob): string {
  return [job.label, job.sub_menu, job.category_key, categoryWords(job.category_key ?? ''), aliasText(job.aliases)]
    .join(' ')
    .toLowerCase();
}

/**
 * A code matches by whole segment ("dcm" → BF-DCM-RR-115), or as a whole
 * (a pasted code). Never as a substring: "dcm" must not find BF-AUDCM-….
 */
function codeMatches(code: string | null | undefined, word: string): boolean {
  const c = (code ?? '').toLowerCase();
  if (!c) return false;
  return c === word || c.split('-').includes(word);
}

export function catalogJobMatchesQuery(job: SearchableCatalogJob, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  const text = catalogJobSearchText(job);
  return words.every((w) => text.includes(w) || codeMatches(job.code, w));
}
